import { useMemo, useState } from 'react';
import type { PdfDocumentMeta, Project } from '@canvas/shared-types';
import { MOVIE_STUDIO_NAME } from '@/data/movieProduct';
import type { AuthUser } from '@/hooks/useAuth';
import { isMovieProject } from '@/lib/movieProject';
import NewMovieModal from './movie/NewMovieModal';
import NewProjectModal from './NewProjectModal';

export type DashTab = 'projects' | 'movie' | 'pdf';

interface DashboardProps {
  user: AuthUser;
  projects: Project[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  pdfDocs: PdfDocumentMeta[];
  pdfDocsLoading: boolean;
  onLogout: () => void;
  onOpenProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => Promise<void>;
  onCreateProject: (input: {
    name: string;
    description: string;
    width: number;
    height: number;
    tags: string[];
  }) => Promise<void>;
  onCreateMovie: (input: { name: string; templateId: string }) => Promise<void>;
  onMakeMovie: (projectId: string) => Promise<void>;
  /** Tab to show when the dashboard mounts again after leaving a studio. */
  initialTab?: DashTab;
  onTabChange?: (tab: DashTab) => void;
  onOpenPdf: () => void;
  onUploadImages: () => void;
  onScan: () => void;
  onOpenSavedPdf: (docId: string) => void;
  onDeletePdf: (docId: string) => void;
  /** Shown while a saved PDF is downloading from Storage */
  openingPdfId?: string | null;
}

export default function Dashboard({
  user,
  projects,
  loading,
  error,
  creating,
  pdfDocs,
  pdfDocsLoading,
  onLogout,
  onOpenProject,
  onDeleteProject,
  onCreateProject,
  onCreateMovie,
  onMakeMovie,
  initialTab = 'projects',
  onTabChange,
  onOpenPdf,
  onUploadImages,
  onScan,
  onOpenSavedPdf,
  onDeletePdf,
  openingPdfId = null,
}: DashboardProps) {
  const [tab, setTab] = useState<DashTab>(initialTab);
  const [query, setQuery] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showNewMovie, setShowNewMovie] = useState(false);

  const designProjects = useMemo(
    () => projects.filter((project) => !isMovieProject(project)),
    [projects]
  );
  const movieProjects = useMemo(
    () => projects.filter((project) => isMovieProject(project)),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = tab === 'movie' ? movieProjects : designProjects;
    if (!q) return list;
    return list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.tags?.some((tag) => tag.toLowerCase().includes(q))
    );
  }, [designProjects, movieProjects, query, tab]);

  const filteredPdfs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pdfDocs;
    return pdfDocs.filter((d) => d.name.toLowerCase().includes(q));
  }, [pdfDocs, query]);

  const initials = user.displayName
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const switchTab = (next: DashTab) => {
    setTab(next);
    setQuery('');
    onTabChange?.(next);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand">
          <img
            className="brand-mark"
            src="/brand/icon.png"
            alt=""
            width={32}
            height={32}
          />
          <span className="brand-text">
            Craftara <span>Studio</span>
          </span>
        </div>
        <div className="user-chip">
          <div className="user-avatar" aria-hidden>
            {initials || 'U'}
          </div>
          <div className="user-meta">
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </div>
          <button
            type="button"
            className="logout-icon-btn"
            onClick={onLogout}
            title="Sign out"
            aria-label="Sign out"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4v-2H6V6h4V4zm3.59 5.41L15 8l5 4-5 4-1.41-1.41L15.17 13H9v-2h6.17l-1.58-1.59z"
              />
            </svg>
          </button>
        </div>
      </header>

      <main className="app-main dash-home">
        <div className="dash-top">
          <div>
            <h1>Workspace</h1>
            <p>Design canvases, movies, and PDF tools in one place.</p>
          </div>
        </div>

        <div className="dash-tabs" role="tablist" aria-label="Workspace sections">
          <button
            type="button"
            role="tab"
            id="tab-projects"
            aria-selected={tab === 'projects'}
            aria-controls="panel-projects"
            className={`dash-tab ${tab === 'projects' ? 'active' : ''}`}
            onClick={() => switchTab('projects')}
          >
            My projects
            <span className="dash-tab-count">{designProjects.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="tab-movie"
            aria-selected={tab === 'movie'}
            aria-controls="panel-movie"
            className={`dash-tab ${tab === 'movie' ? 'active' : ''}`}
            onClick={() => switchTab('movie')}
          >
            {MOVIE_STUDIO_NAME}
            <span className="dash-tab-count">{movieProjects.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="tab-pdf"
            aria-selected={tab === 'pdf'}
            aria-controls="panel-pdf"
            className={`dash-tab ${tab === 'pdf' ? 'active' : ''}`}
            onClick={() => switchTab('pdf')}
          >
            PDF Studio
            <span className="dash-tab-count">{pdfDocs.length}</span>
          </button>
        </div>

        {error && (
          <div className="error-banner" style={{ marginBottom: '1rem' }} role="alert">
            {error}
          </div>
        )}

        {(tab === 'projects' || tab === 'movie') && (
          <section
            id={tab === 'movie' ? 'panel-movie' : 'panel-projects'}
            role="tabpanel"
            aria-labelledby={tab === 'movie' ? 'tab-movie' : 'tab-projects'}
            className="dash-panel"
          >
            <div className="dash-toolbar">
              <input
                className="search-input"
                type="search"
                placeholder={tab === 'movie' ? 'Search movies…' : 'Search projects…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={tab === 'movie' ? 'Search movies' : 'Search projects'}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => (tab === 'movie' ? setShowNewMovie(true) : setShowNew(true))}
              >
                {tab === 'movie' ? 'New movie' : 'New project'}
              </button>
            </div>

            {tab === 'movie' && (
              <p className="dash-panel-hint">
                Scenes, animation, and video export live here. A design stays in My projects.
              </p>
            )}

            {loading ? (
              <div className="loading-screen dash-loading">Loading projects…</div>
            ) : filteredProjects.length === 0 ? (
              <div className="empty-state dash-empty">
                <h2>
                  {query
                    ? 'No matches'
                    : tab === 'movie'
                      ? 'No movies yet'
                      : 'No projects yet'}
                </h2>
                <p>
                  {query
                    ? 'Try a different search term.'
                    : tab === 'movie'
                      ? 'Start a movie at a social size, or make one from a design.'
                      : 'Create a slide, poster, or social asset to get started.'}
                </p>
                {!query && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => (tab === 'movie' ? setShowNewMovie(true) : setShowNew(true))}
                  >
                    {tab === 'movie' ? 'Create your first movie' : 'Create your first project'}
                  </button>
                )}
              </div>
            ) : (
              <ul className="file-icons" aria-label={tab === 'movie' ? 'Movies' : 'Projects'}>
                {filteredProjects.map((project) => (
                  <li key={project.projectId} className="file-icon-item">
                    <button
                      type="button"
                      className={`file-icon-open ${tab === 'projects' ? 'has-side-actions' : ''}`}
                      onClick={() => onOpenProject(project.projectId)}
                      title={project.name}
                    >
                      <span className="file-icon" aria-hidden>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                          <rect
                            x="4"
                            y="3"
                            width="16"
                            height="18"
                            rx="2.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          />
                          <path
                            d="M8 8h8M8 12h8M8 16h5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                      <span className="file-name">{project.name}</span>
                    </button>
                    {tab === 'projects' && (
                    <button
                      type="button"
                      className="file-icon-movie"
                      title="Make a movie"
                      aria-label={`Make a movie from ${project.name}`}
                      disabled={creating}
                      onClick={(e) => {
                        e.stopPropagation();
                        void onMakeMovie(project.projectId);
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden>
                        <rect
                          x="3"
                          y="6"
                          width="13"
                          height="12"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                        <path
                          d="M16 10l5-2.5v9L16 14"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    )}
                    <button
                      type="button"
                      className="file-icon-delete"
                      title="Delete"
                      aria-label={`Delete ${project.name}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          confirm(`Delete “${project.name}”? This cannot be undone.`)
                        ) {
                          await onDeleteProject(project.projectId);
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden>
                        <path
                          d="M5 7h14M10 7V5h4v2M8 7l1 12h6l1-12"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {tab === 'pdf' && (
          <section
            id="panel-pdf"
            role="tabpanel"
            aria-labelledby="tab-pdf"
            className="dash-panel"
          >
            <div className="dash-toolbar">
              <input
                className="search-input"
                type="search"
                placeholder="Search PDFs…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search PDFs"
              />
              <div className="dash-toolbar-actions">
                <button type="button" className="btn btn-primary" onClick={onOpenPdf}>
                  Open PDF
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onUploadImages}
                >
                  Upload images
                </button>
                <button type="button" className="btn btn-secondary" onClick={onScan}>
                  Scan
                </button>
              </div>
            </div>

            <p className="dash-panel-hint">
              Annotate, sign, and protect PDFs — or send pages into a design project.
            </p>

            {pdfDocsLoading ? (
              <div className="loading-screen dash-loading">Loading PDFs…</div>
            ) : filteredPdfs.length === 0 ? (
              <div className="empty-state dash-empty">
                <h2>{query ? 'No matches' : 'No saved PDFs yet'}</h2>
                <p>
                  {query
                    ? 'Try a different search term.'
                    : 'Open a PDF, upload images, or scan to get started.'}
                </p>
                {!query && (
                  <button type="button" className="btn btn-primary" onClick={onOpenPdf}>
                    Open a PDF
                  </button>
                )}
              </div>
            ) : (
              <ul className="file-icons" aria-label="Saved PDFs">
                {filteredPdfs.map((doc) => {
                  const opening = openingPdfId === doc.docId;
                  return (
                    <li key={doc.docId} className="file-icon-item">
                      <button
                        type="button"
                        className="file-icon-open"
                        onClick={() => onOpenSavedPdf(doc.docId)}
                        disabled={!!openingPdfId}
                        title={opening ? `Opening ${doc.name}…` : doc.name}
                      >
                        <span className="file-icon file-icon-pdf" aria-hidden>
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                            <path
                              d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M14 3v5h5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.5 14.5h7M8.5 17.5h5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <span className="file-name">
                          {opening ? 'Opening…' : doc.name}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="file-icon-delete"
                        title="Delete"
                        aria-label={`Delete ${doc.name}`}
                        disabled={!!openingPdfId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete “${doc.name}”?`)) onDeletePdf(doc.docId);
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden>
                          <path
                            d="M5 7h14M10 7V5h4v2M8 7l1 12h6l1-12"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>

      {showNew && (
        <NewProjectModal
          busy={creating}
          onClose={() => setShowNew(false)}
          onCreate={async (input) => {
            await onCreateProject(input);
            setShowNew(false);
          }}
        />
      )}

      {showNewMovie && (
        <NewMovieModal
          busy={creating}
          onClose={() => setShowNewMovie(false)}
          onCreate={async (input) => {
            await onCreateMovie(input);
            setShowNewMovie(false);
          }}
        />
      )}
    </div>
  );
}
